import { memo, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { useIntl } from 'react-intl'
import { capitalise } from 'lib/string/stringUtils'
import { trackClientEvent } from 'analytics/analytics'
import { chatExploreCategories, type ChatExploreCategory } from 'tripPlanner/pages/chat/chatExploreCategories'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'

interface Props {
  /** The search query to display as the filter label */
  hotelSearchQuery: string
  /** Whether the hotel filter is visible */
  isHotelFilterVisible: boolean
  /** Called when the hotel filter is dismissed */
  onHotelFilterDismiss: () => void
  /** The experience search query to display as the filter label */
  experienceSearchQuery: string
  /** Whether the experience filter is visible */
  isExperienceFilterVisible: boolean
  /** Called when the experience filter is dismissed */
  onExperienceFilterDismiss: () => void
  /** Whether the explore filters are visible */
  isExploreFiltersVisible: boolean
  /** Whether explore filters are active (a category selected) */
  isExploreFilterActive: boolean
  /** Current explore category selection */
  selectedExploreCategory?: ChatExploreCategory | undefined
  /** Called when explore category changes */
  onExploreCategorySelect: (category: ChatExploreCategory | undefined) => void
  /** Whether explore data is loading */
  isExploreLoading: boolean
  /** Whether explore results exist for current bounds */
  hasExploreResults: boolean
  /** Whether map bounds changed since last applied */
  isBoundsDirty: boolean
}

const ExploreFiltersRow = styled.div`
  width: 100%;

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
`

const OverlayFilterChip = styled(FilterChip)`
  && {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    --chip-stroke-color: ${props => props.theme.palette.neutral.surface.darkForeground};
  }
  &&:hover {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
    --chip-stroke-color: ${props => props.theme.palette.neutral.surface.darkForeground};
  }
`

const ExploreStatusLabel = styled.span`
  font-size: ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  padding-left: ${rem(4)};
`

interface ExploreCategoryChipProps {
  category: ChatExploreCategory
  isSelected: boolean
  onSelect: (category: ChatExploreCategory) => void
}

const ExploreCategoryChip = memo(function ExploreCategoryChip({
  category,
  isSelected,
  onSelect,
}: ExploreCategoryChipProps) {
  const intl = useIntl()
  const { key, labelMessage, icon: Icon } = category
  const handleClick = useCallback(() => {
    onSelect(category)
  }, [category, onSelect])

  return (
    <FilterChip
      key={key}
      selected={isSelected}
      size="medium"
      startIcon={<Icon />}
      onClick={handleClick}
    >
      {intl.formatMessage(labelMessage)}
    </FilterChip>
  )
})

/**
 * ChatMapFilters displays dynamic filter chips above the map.
 * Supports hotel search and explore category filters.
 */
function ChatMapFilters({
  hotelSearchQuery,
  isHotelFilterVisible,
  onHotelFilterDismiss,
  experienceSearchQuery,
  isExperienceFilterVisible,
  onExperienceFilterDismiss,
  isExploreFiltersVisible,
  isExploreFilterActive,
  selectedExploreCategory,
  onExploreCategorySelect,
  isExploreLoading,
  hasExploreResults,
  isBoundsDirty,
}: Props) {
  const intl = useIntl()
  const handleExploreCategorySelect = useCallback((category: ChatExploreCategory) => {
    const isDeselect = selectedExploreCategory?.key === category.key

    trackClientEvent({
      subject: 'chat_map_explore_filter',
      action: isDeselect ? 'deselect' : `select_${category.key.replace(/-/g, '_')}`,
      category: 'chat_trip_planner',
      type: 'interaction',
    })

    if (isDeselect) {
      onExploreCategorySelect(undefined)
      return
    }
    onExploreCategorySelect(category)
  }, [onExploreCategorySelect, selectedExploreCategory])

  const handleHotelFilterDismiss = useCallback(() => {
    trackClientEvent({
      subject: 'chat_map_hotel_filter',
      action: 'dismiss',
      category: 'chat_trip_planner',
      type: 'interaction',
    })
    onHotelFilterDismiss()
  }, [onHotelFilterDismiss])

  const handleExperienceFilterDismiss = useCallback(() => {
    trackClientEvent({
      subject: 'chat_map_experience_filter',
      action: 'dismiss',
      category: 'chat_trip_planner',
      type: 'interaction',
    })
    onExperienceFilterDismiss()
  }, [onExperienceFilterDismiss])

  const showHotelFilter = isHotelFilterVisible && Boolean(hotelSearchQuery)
  const showExperienceFilter = isExperienceFilterVisible && Boolean(experienceSearchQuery)
  const showExploreFilters = isExploreFiltersVisible
  const showExploreStatus = showExploreFilters &&
    isExploreFilterActive &&
    !isExploreLoading &&
    !hasExploreResults &&
    !isBoundsDirty

  if (!showHotelFilter && !showExperienceFilter && !showExploreFilters) {
    return null
  }

  return (
    <Group direction="vertical" gap={8} fullWidth>
      {showHotelFilter && (
        <Group direction="horizontal" gap={8} wrap="wrap">
          <OverlayFilterChip
            size="medium"
            onClick={handleHotelFilterDismiss}
            startIcon={<LineTimesIcon size="XS" colour="dark-contrast" />}
          >
            {intl.formatMessage({
              id: 'ep7MfYGZ4XhDNlmp7H7H',
              defaultMessage: 'Hotels | {query}',
              description: 'Hotel filter chip label showing the search query on the chat map',
            }, { query: capitalise(hotelSearchQuery) })}
          </OverlayFilterChip>
        </Group>
      )}
      {showExperienceFilter && (
        <Group direction="horizontal" gap={8} wrap="wrap">
          <OverlayFilterChip
            size="medium"
            onClick={handleExperienceFilterDismiss}
            startIcon={<LineTimesIcon size="XS" colour="dark-contrast" />}
          >
            {intl.formatMessage({
              id: 'B3Pa_hXN4rBikMx1F4l6',
              defaultMessage: 'Experiences | {query}',
              description: 'Experience filter chip label showing the search query on the chat map',
            }, { query: capitalise(experienceSearchQuery) })}
          </OverlayFilterChip>
        </Group>
      )}
      {showExploreFilters && (
        <ExploreFiltersRow className={clsx(isExploreLoading && 'disabled')}>
          <CardCarousel
            gap={8}
            gutterSize={24}
            desktopGutterSize={40}
            gutterStyle="overflow"
            snap="start"
            pageSize="auto"
            arrowsOnlyOnHover
          >
            {chatExploreCategories.map((category) => (
              <ExploreCategoryChip
                key={category.key}
                category={category}
                isSelected={selectedExploreCategory?.key === category.key}
                onSelect={handleExploreCategorySelect}
              />
            ))}
          </CardCarousel>
          {showExploreStatus && (
            <ExploreStatusLabel>{intl.formatMessage({
              id: 'Vy-1FxksAHXUbNlVPQWx',
              defaultMessage: 'No results in this area',
              description: 'Status message when no explore results found in the current map area',
            })}
            </ExploreStatusLabel>
          )}
        </ExploreFiltersRow>
      )}
    </Group>
  )
}

export default memo(ChatMapFilters)
